import React from 'react';

import { SubscriptionDto, PlanType, SubscriptionStatus } from '@optidev/shared-types';
import { CreditDetailsResponse } from '../../../lib/api/credits';
import { usePermissions } from '../../../hooks/usePermissions';
import { getActiveBridge, CHILD_ACTION } from '../../../embed/OptiSignsBridge';

import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Loader2, ExternalLink, AlertTriangle, CreditCard } from 'lucide-react';

interface EmbedBillingViewProps {
  subscription: SubscriptionDto | null;
  creditDetails: CreditDetailsResponse | null;
  creditsLoading: boolean;
  onTopUp: () => void;
}

export const EmbedBillingView: React.FC<EmbedBillingViewProps> = ({
  subscription,
  creditDetails,
  creditsLoading,
  onTopUp,
}) => {
  const { isAdminOrOwner } = usePermissions();

  const planType = subscription?.planType as string || PlanType.FREE;
  const status = subscription?.status as string;
  const isEmbed = planType === PlanType.EMBED;
  const isTrialing = status === SubscriptionStatus.TRIALING;
  const isActive = status === SubscriptionStatus.ACTIVE;
  const isCancelled = status === SubscriptionStatus.CANCELLED;
  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd;

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleOpenBilling = (): void => {
    const bridge = getActiveBridge();
    if (bridge) {
      bridge.sendToParent(CHILD_ACTION.OPEN_BILLING);
    }
  };

  const canTopUp = isEmbed && isActive;

  const handleBuyCredits = (): void => {
    if (canTopUp) {
      onTopUp();
    } else {
      handleOpenBilling();
    }
  };

  const getStatusLabel = (): string => {
    if (isTrialing) return 'Free trial';
    if (isActive && !cancelAtPeriodEnd) return 'Active subscription';
    if (isActive && cancelAtPeriodEnd) return 'Subscription ending soon';
    if (isCancelled) return 'Subscription inactive';
    return 'OptiDev credits';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-5 border-b border-border/40">
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
          <CreditCard className="h-5 w-5 text-muted-foreground" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Credits & Billing</h2>
          <p className="text-sm text-muted-foreground">{getStatusLabel()}</p>
        </div>
      </div>

      {/* Subscription Card */}
      <Card className="border-neutral-200 dark:border-neutral-800 bg-muted/30">
        <CardContent className="p-4">
          {/* Top row: plan info + manage button */}
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {isTrialing ? 'OptiDev Trial' : isEmbed ? 'OptiDev Add-on' : 'OptiDev'}
                </span>
                {isTrialing && creditDetails?.nextResetAt && (
                  <span className="text-xs text-muted-foreground">
                    Trial ends {formatDate(creditDetails.nextResetAt)}
                  </span>
                )}
                {isActive && !cancelAtPeriodEnd && creditDetails?.nextResetAt && (
                  <span className="text-xs text-muted-foreground">
                    Renews {formatDate(creditDetails.nextResetAt)}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isTrialing && '14-day trial with 100 credits included'}
                {isActive && !cancelAtPeriodEnd && 'Subscription active'}
                {isActive && cancelAtPeriodEnd && `Ends ${formatDate(subscription?.currentPeriodEnd)} — will be cancelled at end of billing period`}
                {isCancelled && 'Remaining credits still available. Reactivate to continue.'}
                {!isEmbed && 'Activate OptiDev to get started'}
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={handleOpenBilling}
              className="gap-1.5 shrink-0 text-xs h-8 px-3 text-foreground/70 hover:text-foreground border border-neutral-200 dark:border-neutral-700"
            >
              Open Billing
              <ExternalLink className="h-3 w-3" />
            </Button>
          </div>

          {/* Credits row */}
          {creditsLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="pt-3 border-t border-neutral-200/60 dark:border-neutral-800/60">
              {isTrialing ? (
                /* Trial: single column */
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Trial Credits</p>
                  <p className="text-lg font-semibold tabular-nums">
                    {creditDetails?.subscriptionBalance?.toFixed(2) ?? '0.00'}
                  </p>
                </div>
              ) : (
                /* Paid / cancelled: 3-column grid */
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Credits Available</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {creditDetails?.balance?.toFixed(2) ?? '0.00'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Plan credits (resets each billing cycle)</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {creditDetails?.subscriptionBalance?.toFixed(2) ?? '0.00'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Top-up credits (never expires)</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {creditDetails?.topupBalance?.toFixed(2) ?? '0.00'}
                    </p>
                  </div>
                </div>
              )}

            </div>
          )}
        </CardContent>
      </Card>

      {/* Warnings */}
      {cancelAtPeriodEnd && subscription?.currentPeriodEnd && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-600 dark:text-amber-400">Subscription ending</p>
            <p className="text-muted-foreground">
              Your subscription ends on {formatDate(subscription.currentPeriodEnd)}. Credits will stop refreshing after this date.
            </p>
          </div>
        </div>
      )}

      {isCancelled && isEmbed && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border/50">
          <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Subscription inactive</p>
            <p className="text-muted-foreground">
              Your remaining credits are still available. Reactivate to get monthly credits.
            </p>
          </div>
        </div>
      )}

      {/* Top-up / Get Add-on CTA */}
      <div className="flex flex-col gap-3 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Need more credits?</p>
            <p className="text-xs text-muted-foreground">
              {canTopUp
                ? 'One-time purchase, never expires'
                : isTrialing
                  ? 'Activate your subscription to purchase credits'
                  : 'Activate OptiDev to get started'}
            </p>
          </div>
          <Button
            onClick={handleBuyCredits}
            disabled={!isAdminOrOwner}
            className="text-xs h-8 px-3 bg-foreground hover:bg-foreground/90 text-background"
          >
            {canTopUp ? 'Top up credits' : 'Get OptiDev Add-on'}
          </Button>
        </div>
      </div>
    </div>
  );
};
